/**
 * Avatar.tsx — THE avatar. One component, every module.
 *
 * WHY THIS EXISTS. A read-only inventory on 2026-08-19 found 76 places in this app
 * that draw a person's photo or their initials, and no shared component behind any
 * of them: 7 near-identical local `Avatar`s, 12 copies of `initials()`, 6 copies of
 * a colour helper across 5 palettes. Every one of them was written because the next
 * screen needed an avatar and there was nothing to reach for. This is the thing to
 * reach for. See `lib/avatar.ts` for the pure half (initials, radius, tints).
 *
 * ── THE SHAPE RULE (Lis, 2026-08-19) ──────────────────────────────────────────
 *
 * Avatars are SQUARE-ROUNDED, not circular, everywhere in the app. `shape="circle"`
 * exists only as an escape hatch and has no caller; if you find yourself reaching
 * for it, that is the conversation to have with Lis rather than a prop to set.
 *
 * Radius comes from the ladder (`avatarRadius`) for anything converted from a
 * circle. An avatar that was ALREADY square-rounded passes `radius` explicitly and
 * keeps the exact value it had — the migration was scoped to change shape, never
 * appearance, so a 40px/7px card stays 40px/7px.
 *
 * ── WHAT THIS COMPONENT DOES NOT DECIDE ───────────────────────────────────────
 *
 * Colour. Six palettes are in use and they disagree; picking one here would repaint
 * screens nobody asked to have repainted. `background`/`color`/`className` are the
 * caller's, and every migrated site passes what it already painted. Consolidating
 * the palettes is a design decision for Lis, deliberately left undone.
 *
 * ── IMAGES ────────────────────────────────────────────────────────────────────
 *
 * `src` is a STORED VALUE, not a URL: a storage path (everything written since
 * migration 0170), a legacy public URL, or an external URL. It is minted into a
 * short-lived signed URL at render time by <StorageImage> and never persisted.
 * While that mint is in flight the initials show, then the photo appears a beat
 * later — the price of a private bucket, and the reason initials are the fallback
 * rather than a grey box.
 *
 * Pass `imageSrc` instead when the caller already holds a plain, directly-loadable
 * URL (a signed URL it minted itself, a data: URI, a local preview) — that skips
 * the storage layer entirely.
 */

import React from 'react';
import { StorageImage } from './StorageImage';
import { avatarFontSize, avatarRadius, initialsOf } from '../../lib/avatar';

export type AvatarShape = 'rounded' | 'circle';

export interface AvatarProps {
  /** Display name — drives the initials and any `title` the caller wants. */
  name?: string | null;
  /**
   * The STORED image value: a storage path, a legacy public URL, or an external
   * URL. Signed at render by <StorageImage>. Use `imageSrc` for a plain URL.
   */
  src?: string | null;
  /** A plain, directly-loadable URL. Wins over `src` when both are given. */
  imageSrc?: string | null;
  /** Which storage bucket `src` lives in. Defaults to record-avatars. */
  bucket?: string;
  /** Diameter in px. */
  size?: number;
  /**
   * Corner radius. Omit and the ladder decides (`avatarRadius(size)`). Pass
   * explicitly ONLY to preserve an avatar that was already square-rounded — a
   * number of pixels, or a token string such as `var(--p-r-sm)` where the site was
   * already written against the theme's radius scale.
   */
  radius?: number | string;
  /** `circle` is an escape hatch with no caller — see the shape rule above. */
  shape?: AvatarShape;
  /** 2 letters for a person, 1 for an entity that reads as a single mark. */
  letters?: 1 | 2;
  /**
   * Explicit initials, when the caller's rule is not "first letters of the first
   * two words" — a contact whose name lives in two columns, for instance.
   */
  initials?: string;
  /** Rendered instead of initials when there is no image — a company's icon mark. */
  icon?: React.ReactNode;
  /** Fallback-chip background. Any CSS colour, token, or gradient. */
  background?: string;
  /** Initials colour. */
  color?: string;
  /** Border on BOTH the photo and the initials chip — the avatar-stack's ring. */
  border?: string;
  /**
   * Border on the PHOTO ONLY.
   *
   * This is the app's prevailing pattern and it is not an accident: a photograph
   * needs an edge to separate it from the surface behind it, a saturated initials
   * chip does not. Every CRM avatar drew it this way. It also matters while a signed
   * URL is still being minted — the initials stand in for a beat, and a border that
   * appeared only once the photo arrived would make every avatar flicker.
   */
  imageBorder?: string;
  /** Overrides the derived initials type size. */
  fontSize?: number;
  /** Overrides the default 700. */
  fontWeight?: number;
  /** Extra classes — how a Tailwind-coloured site keeps its `bg-teal-500`. */
  className?: string;
  /** Merged last, so a caller can still reach anything not covered by a prop. */
  style?: React.CSSProperties;
  /** Native tooltip. */
  title?: string;
  /**
   * Alt text for the photo. Default '' — an avatar beside its own name is
   * decorative, and a screen reader reading the name twice is worse than silence.
   */
  alt?: string;
  /** Hide the whole thing from assistive tech (name already read out alongside). */
  decorative?: boolean;
  /** Escape hatch for tests and probes. */
  'data-testid'?: string;
}

/**
 * A person's or company's avatar: photo when there is one, initials when there is
 * not, at a square-rounded corner.
 */
export function Avatar({
  name,
  src,
  imageSrc,
  bucket,
  size = 32,
  radius,
  shape = 'rounded',
  letters = 2,
  initials,
  icon,
  background = 'var(--p-accent)',
  color = 'var(--p-on-accent)',
  border,
  imageBorder,
  fontSize,
  fontWeight = 700,
  className,
  style,
  title,
  alt = '',
  decorative,
  'data-testid': testId,
}: AvatarProps): React.ReactElement {
  const r = shape === 'circle' ? '50%' : (radius ?? avatarRadius(size));

  /* Every branch below shares this box, which is what makes the shape change safe:
     width and height are set once, from one prop, so a shape-only migration cannot
     accidentally resize anything. `flex: none` stops a cramped flex row squashing
     an avatar into an ellipse — a bug the old one-off circles hit repeatedly.

     DELIBERATELY ABSENT: `display: block` on the photo, and `line-height: 1` /
     `overflow: hidden` on the chip. None of the 76 sites set them, and adding them
     here would be a silent appearance change smuggled in behind a shape migration —
     `display: block` in particular removes an inline image's baseline gap, which
     shortens its parent by a few pixels anywhere the avatar is not in a flex row.
     A caller that wants any of them passes `style`. */
  const box: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: r,
    flex: 'none',
    ...(border ? { border } : null),
  };

  /* The photo's box, which may carry an edge the initials chip does not. */
  const imageBox: React.CSSProperties = imageBorder ? { ...box, border: imageBorder } : box;

  const label = initials ?? initialsOf(name, letters);

  const fallback = (
    <span
      className={className}
      title={title}
      aria-hidden={decorative || !title ? true : undefined}
      data-testid={testId}
      style={{
        ...box,
        display: 'grid',
        placeItems: 'center',
        background,
        color,
        fontSize: fontSize ?? avatarFontSize(size),
        fontWeight,
        ...style,
      }}
    >
      {icon ?? label}
    </span>
  );

  /* A plain URL the caller already holds — no signing, no storage layer. Used by
     the upload control's local preview and by anything holding a data: URI. */
  if (imageSrc) {
    return (
      <img
        src={imageSrc}
        alt={alt}
        title={title}
        aria-hidden={decorative ? true : undefined}
        data-testid={testId}
        className={className}
        style={{ ...imageBox, objectFit: 'cover', ...style }}
      />
    );
  }

  if (src) {
    return (
      <StorageImage
        value={src}
        bucket={bucket}
        alt={alt}
        title={title}
        aria-hidden={decorative ? true : undefined}
        data-testid={testId}
        className={className}
        fallback={fallback}
        style={{ ...imageBox, objectFit: 'cover', ...style }}
      />
    );
  }

  return fallback;
}

export default Avatar;
